// client/src/components/media/MentionInput.jsx
// Textarea for social-media post content with @mention support.
// Mentions target platform handles (Twitter, Instagram, etc.) — not internal users.
// Provides a visual highlight overlay so @handles stand out while typing,
// and an optional recent-handles dropdown for quick re-use.

import React, { useState, useRef, useEffect, useCallback } from 'react';

const MENTION_REGEX = /@([a-zA-Z0-9_.]*)$/;
const STORAGE_KEY = 'onscreen_recent_handles';
const MAX_RECENT = 20;

// ---------------------------------------------------------------------------
// Persist / retrieve recently-used handles in localStorage
// ---------------------------------------------------------------------------
function getRecentHandles() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveHandle(handle) {
  if (!handle) return;
  const clean = handle.replace(/^@/, '').trim();
  if (!clean) return;
  try {
    let list = getRecentHandles().filter(h => h !== clean);
    list.unshift(clean);
    if (list.length > MAX_RECENT) list = list.slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch { /* storage full – ignore */ }
}

export default function MentionInput({
  value,
  onChange,
  placeholder = '',
  rows = 5,
  className = 'form-control',
}) {
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Detect @mention trigger from cursor position
  const detectMention = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return null;
    const cursorPos = ta.selectionStart;
    const textBefore = value.slice(0, cursorPos);
    const match = textBefore.match(MENTION_REGEX);
    return match ? match[1] : null;
  }, [value]);

  // Filter recent handles when user types @
  useEffect(() => {
    const query = detectMention();
    if (query !== null) {
      const recent = getRecentHandles();
      const filtered = query.length === 0
        ? recent.slice(0, 8)
        : recent.filter(h => h.toLowerCase().startsWith(query.toLowerCase())).slice(0, 8);
      setSuggestions(filtered);
      setShowDropdown(filtered.length > 0);
      setSelectedIdx(0);
    } else {
      setShowDropdown(false);
      setSuggestions([]);
    }
  }, [value, detectMention]);

  // Insert the selected handle
  const insertHandle = useCallback((handle) => {
    const ta = textareaRef.current;
    if (!ta) return;

    const cursorPos = ta.selectionStart;
    const textBefore = value.slice(0, cursorPos);
    const textAfter = value.slice(cursorPos);

    const match = textBefore.match(MENTION_REGEX);
    if (!match) return;

    const atPos = cursorPos - match[0].length;
    const newText = textBefore.slice(0, atPos) + `@${handle} ` + textAfter;

    onChange(newText);
    saveHandle(handle);
    setShowDropdown(false);
    setSuggestions([]);

    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        const newPos = atPos + handle.length + 2;
        ta.setSelectionRange(newPos, newPos);
      }
    });
  }, [value, onChange]);

  // On blur, persist any new handles that were typed manually
  const handleBlur = useCallback(() => {
    const mentions = value.match(/@([a-zA-Z][a-zA-Z0-9_.]*)/g);
    if (mentions) {
      mentions.forEach(m => saveHandle(m.slice(1)));
    }
    // Small delay so click on dropdown item fires before we hide
    setTimeout(() => setShowDropdown(false), 150);
  }, [value]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (suggestions[selectedIdx]) {
        e.preventDefault();
        insertHandle(suggestions[selectedIdx]);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          textareaRef.current && !textareaRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={textareaRef}
        className={className}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
      />

      {showDropdown && suggestions.length > 0 && (
        <div ref={dropdownRef} style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          right: 0,
          maxHeight: 200,
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1050,
          marginBottom: 4,
        }}>
          <div style={{
            padding: '4px 12px',
            fontSize: 11,
            color: '#6c757d',
            borderBottom: '1px solid #f0f0f0',
            background: '#f8f9fa',
          }}>
            Recent handles
          </div>
          {suggestions.map((handle, i) => (
            <div
              key={handle}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                background: i === selectedIdx ? '#e8f0fe' : 'transparent',
                borderBottom: '1px solid #f0f0f0',
              }}
              onMouseEnter={() => setSelectedIdx(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                insertHandle(handle);
              }}
            >
              @{handle}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
